import { STATIONS, formatDuration, formatPace, type Plan } from "@roxpacer/core";
import { Fragment } from "react";
import { useEstimate } from "../lib/use-estimate";

/** Read-only per-leg targets with the elapsed time to beat at the end of each leg. */
export function RaceCardTable({ plan, large = false }: { plan: Plan; large?: boolean }) {
  const estimate = useEstimate(plan);
  const names = new Map(plan.athletes.map((a) => [a.id, a.name]));
  const segment = (id: string) => estimate.segments.find((s) => s.id === id);
  const cell = large ? "py-2 sm:py-2.5" : "py-1.5";

  return (
    <table className={`w-full ${large ? "text-base sm:text-lg" : "text-sm"}`}>
      <thead>
        <tr className="text-left text-xs text-muted">
          <th className="py-1 pr-2 font-normal">#</th>
          <th className="pr-2 font-normal">Station</th>
          <th className="pr-2 text-right font-normal">Run</th>
          <th className="pr-2 text-right font-normal">Rox in</th>
          <th className="pr-2 text-right font-normal">Station</th>
          <th className="pr-2 text-right font-normal">Rox out</th>
          <th className="text-right font-normal">Done by</th>
        </tr>
      </thead>
      <tbody>
        {STATIONS.map((station, i) => {
          const leg = i + 1;
          const target = plan.stations[i]!;
          const stationSegment = segment(`station-${leg}`);
          const handover = estimate.segments.find((s) => s.type === "handover" && s.leg === leg);
          const athlete = names.get(stationSegment?.athleteIds[0] ?? "");
          const split =
            plan.format === "doubles" && target.split
              ? target.split.map((s) => Math.round(s.pct)).join("/")
              : null;
          return (
            <Fragment key={station.id}>
              <tr className="border-t border-line align-baseline">
                <td className={`${cell} pr-2 text-muted tabular-nums`}>{leg}</td>
                <td className={`${cell} pr-2`}>
                  <span className="font-medium">{station.name}</span>
                  {plan.format === "relay" && (
                    <span className="block text-xs text-ink-2">{athlete}</span>
                  )}
                  {split && <span className="block text-xs text-ink-2">Split {split}</span>}
                </td>
                <td className={`${cell} pr-2 text-right tabular-nums`}>
                  {formatPace(plan.runs[i]!.paceSecPerKm)}
                </td>
                <td className={`${cell} pr-2 text-right text-ink-2 tabular-nums`}>
                  {formatDuration(target.roxzoneInSec)}
                </td>
                <td className={`${cell} pr-2 text-right tabular-nums`}>
                  {formatDuration(target.targetSec)}
                </td>
                <td className={`${cell} pr-2 text-right text-ink-2 tabular-nums`}>
                  {formatDuration(target.roxzoneOutSec)}
                </td>
                <td className={`${cell} text-right font-semibold tabular-nums`}>
                  {formatDuration(stationSegment?.endSec ?? 0)}
                </td>
              </tr>
              {plan.format === "relay" && handover && (
                <tr className="text-xs text-ink-2">
                  <td />
                  <td colSpan={4} className="pb-1.5">
                    Handover to {names.get(handover.athleteIds.at(-1)!)}
                  </td>
                  <td className="pr-2 pb-1.5 text-right tabular-nums">
                    {formatDuration(handover.targetSec)}
                  </td>
                  <td className="pb-1.5 text-right tabular-nums">
                    {formatDuration(handover.endSec)}
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

const FORMAT_LABEL = { singles: "Singles", doubles: "Doubles", relay: "Relay" } as const;
const DIVISION_LABEL = { men: "Men", women: "Women", mixed: "Mixed" } as const;

/** e.g. "Doubles · Open · Mixed · Alex & Sam". */
export function planDescription(plan: Plan): string {
  const athletes = plan.athletes.map((a) => a.name.trim()).filter(Boolean);
  return [
    FORMAT_LABEL[plan.format],
    plan.category === "pro" ? "Pro" : "Open",
    DIVISION_LABEL[plan.division],
    plan.format === "singles" ? null : athletes.join(plan.format === "doubles" ? " & " : ", "),
  ]
    .filter(Boolean)
    .join(" · ");
}
